// src/context/UserContext.tsx
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { fetchUserStreak } from "../services/api";

export interface UserInfo {
  id: number;
  name: string;
  email: string;
  picture: string;
  admin: boolean;
  streak_level?: string;
  current_streak?: number;
  best_streak?: number;
}

interface UserContextType {
  user: UserInfo | null;
  setUser: (user: UserInfo | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserInfo | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("jwt");

    if (storedUser) {
      const parsedUser: UserInfo = JSON.parse(storedUser);

      const loadStreak = async () => {
        if (!token) {
          setUser(parsedUser);
          return;
        }

        try {
          const streakData = await fetchUserStreak();

          // ✅ Merge propre : on garde admin, email, name, etc.
          setUser((prev) => ({
            ...parsedUser, // base: données locales du localStorage
            ...prev,       // garde ce qu'on avait déjà (admin, picture, etc.)
            streak_level: streakData.level,
            current_streak: streakData.current_streak,
            best_streak: streakData.best_streak,
          }));
        } catch (error: any) {
          if (error.response?.status === 401) {
            localStorage.removeItem("jwt");
            localStorage.removeItem("user");
            setUser(null);
          } else {
            setUser(parsedUser); // fallback sans streak
          }
        }
      };

      loadStreak();
    }
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
